import axiosInstance from './axiosInstance';

/**
 * Quotes — priced work a customer authorises before the car is booked in.
 *
 * Only the calls that are ABOUT quotes live here, and that is deliberate. A quote IS a job on the server,
 * so everything it does after it is raised — adding work items, recording notes,
 * reading the detail — goes through `jobApi`. Duplicating those endpoints under a
 * quotes path would mean two clients for one resource, and the second one would drift.
 */

/**
 * Every quote still awaiting a customer decision, as one unpaged list.
 *
 * <p>Still what the PHONE draws its board from. The desktop board moved to
 * {@link listOpenQuotesApi} and {@link listAwaitingQuotesApi} — one lane paged and
 * searched, the other not — once a migration put thousands of quotes here at once.
 */
export const listQuotesApi = async () => {
    const response = await axiosInstance.get('/quotes');
    return response.data;
};

/**
 * The quotes NOT yet sent to the customer — the board's first lane, twenty at a time.
 *
 * 🔑 Twenty a page, because a thousand was too heavy on the wire. The search goes to
 * the SERVER once the typing settles — it decides whether the text is a rego or a name
 * and searches the right column — while the loaded page is narrowed locally on every
 * keystroke in between. `q` is omitted when blank; callers validate it first
 * (utils/searchTerm) and the server refuses the rest with the same sentence.
 *
 * @param {{ q?: string, page?: number, size?: number }} options
 * @returns a Micronaut Page: `{ content, totalSize, pageable, … }`
 */
export const listOpenQuotesApi = async ({ q = '', page = 0, size = OPEN_QUOTES_PAGE_SIZE } = {}) => {
    const params = { page, size };
    if (q && q.trim()) params.q = q.trim();
    const response = await axiosInstance.get('/quotes/open', { params });
    return response.data;
};

/** How many not-yet-sent quotes one page holds. The server caps size at 20. */
export const OPEN_QUOTES_PAGE_SIZE = 20;

/**
 * The quotes waiting on the customer's answer — the board's second lane.
 *
 * 🔑 Deliberately UNFILTERED and UNPAGED, and the person asking for this screen was
 * explicit about that: this is the chase list, and a chase list with a page 2 is a
 * list somebody has already stopped reading.
 */
export const listAwaitingQuotesApi = async () => {
    const response = await axiosInstance.get('/quotes/awaiting-customer');
    return response.data;
};

/**
 * Raise a quote for an existing customer and one of their vehicles.
 *
 * Returns the full job detail so the caller can open it straight into the job view and
 * start adding work items without a second round trip.
 */
export const createQuoteApi = async (data) => {
    const response = await axiosInstance.post('/quotes', data);
    return response.data;
};

/**
 * Send the whole quote to the customer for authorisation.
 *
 * No body: there is nothing to choose. Every priced work item goes, because the quote
 * is what the customer is being asked to authorise. Zero-cost inspection lines are
 * left out by the server — there is nothing to approve at $0.
 */
export const sendQuoteForApprovalApi = async (quoteUuid) => {
    const response = await axiosInstance.post(`/quotes/${quoteUuid}/send-for-approval`);
    return response.data;
};

export default { listQuotesApi, createQuoteApi, sendQuoteForApprovalApi };

/**
 * The workshop's MIGRATED quotes.
 *
 * 🔑 A different thing from getQuotesApi above. That returns Jobs carrying quote = true
 * — priced work this product raised, which already IS a job. These are Quote rows
 * imported from Machanicdesk: what the workshop once quoted, with no job behind it.
 * Nothing displayed them at all until this call existed.
 *
 * Each carries jobUuid, so the screen can show which still have no job — the reason the
 * list is worth showing.
 *
 * 🔒 Paged and capped at 100 by the server. One migration brought 10,589 quotes in a
 * single file, so an unpaged list would serialise every quote and its customer into one
 * response.
 */
export const HISTORICAL_QUOTES_PER_PAGE = 20;

/**
 * @param status one of DRAFT, SENT, APPROVED, DECLINED, SUPERSEDED. The import mapped
 *               Machanicdesk's own words onto these — "invoice created" and "job
 *               created" both became APPROVED — so APPROVED is how a workshop finds the
 *               quotes that were acted on.
 * @param from  YYYY-MM-DD, on the quote's OWN date — the Date column the export carried,
 *              not when it was imported, which is the same instant for all of them and
 *              would narrow nothing.
 * @returns `{ items, page, size, total, totalPages }`
 */
export const getHistoricalQuotesApi = async (
    { status = '', from = '', to = '', customer = '',
      page = 0, size = HISTORICAL_QUOTES_PER_PAGE } = {}) => {

    const params = { page, size };
    if (status) params.status = status;
    if (from) params.from = from;
    if (to) params.to = to;
    // Trimmed, and omitted when blank: a bare `?customer=` is a filter the server would
    // apply, and matching every name against '' is not the same as not filtering.
    if (customer && customer.trim()) params.customer = customer.trim();

    const response = await axiosInstance.get('/quotes/history', { params });
    return response.data;
};

/**
 * Raise a job from a migrated quote.
 *
 * ⚠️ vehicleUuid is required: job.vehicle_id is NOT NULL and a migrated quote names its
 * customer but not their car. Priced lines become work items; zero-value lines do not.
 */
export const createJobForQuoteApi = async (quoteUuid, vehicleUuid) => {
    const response = await axiosInstance.post(
        `/quotes/history/${quoteUuid}/create-job`, { vehicleUuid });
    return response.data;
};
